import { useCallback } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, X } from '@phosphor-icons/react'
import type { VariableCost, VariableCostType } from '../../types'

interface DynamicRowListProps {
  rows: VariableCost[]
  onChange: (rows: VariableCost[]) => void
}

let nextId = 1
function generateId() {
  return `cost-${Date.now()}-${nextId++}`
}

export function DynamicRowList({ rows, onChange }: DynamicRowListProps) {
  const addRow = useCallback(() => {
    onChange([...rows, { id: generateId(), name: '', value: 0, type: 'fixed' }])
  }, [rows, onChange])

  const updateRow = useCallback(
    (id: string, field: keyof VariableCost, value: string | number) => {
      onChange(rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)))
    },
    [rows, onChange],
  )

  const removeRow = useCallback(
    (id: string) => {
      onChange(rows.filter((r) => r.id !== id))
    },
    [rows, onChange],
  )

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div
          key={row.id}
          className="flex items-center gap-2 p-2.5 rounded-xl bg-muted/20 border border-transparent hover:border-border/50 transition-colors"
        >
          <Input
            placeholder="Nome do custo"
            value={row.name}
            onChange={(e) => updateRow(row.id, 'name', e.target.value)}
            className="flex-1 h-8 text-sm bg-transparent border-0 border-b border-border/50 rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary"
          />
          <Input
            type="number"
            placeholder="0"
            value={row.value || ''}
            onChange={(e) => updateRow(row.id, 'value', parseFloat(e.target.value) || 0)}
            className="w-24 h-8 text-sm tabular-nums font-mono bg-input border-border"
          />
          <Tabs
            value={row.type}
            onValueChange={(v) => updateRow(row.id, 'type', v as VariableCostType)}
            className="w-auto"
          >
            <TabsList className="h-8 p-0.5 bg-muted gap-0.5">
              <TabsTrigger
                value="fixed"
                className={cn('h-7 w-8 text-xs', 'data-[state=active]:bg-card')}
              >
                R$
              </TabsTrigger>
              <TabsTrigger
                value="percentage"
                className={cn('h-7 w-8 text-xs', 'data-[state=active]:bg-card')}
              >
                %
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => removeRow(row.id)}
          >
            <X weight="regular" className="w-3.5 h-3.5" />
          </Button>
        </div>
      ))}
      <Button
        variant="ghost"
        className="w-full h-8 text-xs text-muted-foreground hover:text-foreground border border-dashed border-border/60 hover:border-border rounded-xl"
        onClick={addRow}
      >
        <Plus weight="regular" className="w-3.5 h-3.5 mr-1.5" />
        Adicionar custo variável
      </Button>
    </div>
  )
}
